import { memo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { tv } from 'tailwind-variants';

const offlineBanner = tv({
  base: [
    'mb-2 flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-sm shadow-[var(--elevation-2)]',
    'border-[var(--color-rail-card-border)] bg-[var(--color-an-surface)] text-[var(--color-an-text)]',
  ].join(' '),
});

export interface OfflineBannerAction {
  readonly label: string;
  readonly onClick: () => void;
}

export interface OfflineBannerLink {
  readonly href: string;
  readonly label: string;
}

export interface OfflineBannerProps {
  readonly message: string;
  readonly action?: OfflineBannerAction;
  /** Optional help/docs chrome link — D18 `.admin-link`. */
  readonly link?: OfflineBannerLink;
  readonly className?: string;
  readonly testId?: string;
  /** Optional leading icon / adornment. */
  readonly leading?: ReactNode;
}

/**
 * Offline connectivity banner (7-state matrix / G27).
 * Always exposes `aria-live="polite"` so assistive tech hears status changes.
 */
export const OfflineBanner = memo<OfflineBannerProps>(
  ({
    message,
    action,
    link,
    className,
    testId = 'admin-offline-banner',
    leading,
  }) => (
    <div
      className={offlineBanner({ className })}
      role="status"
      aria-live="polite"
      data-testid={testId}
      data-tone="warn"
    >
      <p className="prose-measure m-0 flex flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        {leading}
        <span>{message}</span>
        {link !== undefined ? (
          <Link className="admin-link shrink-0" to={link.href} data-testid={`${testId}-link`}>
            {link.label}
          </Link>
        ) : null}
      </p>
      {action !== undefined ? (
        <button
          type="button"
          className="shrink-0 rounded-md border border-[var(--color-rail-card-border)] bg-transparent px-3 py-1.5 text-sm font-semibold"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      ) : null}
    </div>
  ),
);

OfflineBanner.displayName = 'OfflineBanner';
